import React, {useEffect, useState} from 'react';
import Table from '../../../components/common/table/Table';
import {ProjectRequestSort} from '../../../types/project';
import {projectRequestTableAPI} from "../../../services/client";
import {ProjectRequestTableResponseData} from "../../../types/client";
import {Column} from "../../common/table/TableSection";
import DateRangeFilter from '../../common/table/DateRangeFilter';
import {useNavigate} from "react-router-dom";
import {RequestStatus} from "../../../types/request";
import Button from "../../common/button/Button";
import styles from "../../admin/billing/AdminWorkerRequestTable.module.css";

interface DateRange {
    start: Date | null;
    end: Date | null;
}

interface DateRanges {
    create: DateRange;
    update: DateRange;
    confirm: DateRange;
}

//클라이언트 프로젝트 요청 테이블
const ClientProjectRequestTable = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<ProjectRequestTableResponseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isResetting, setIsResetting] = useState(false);
    const [filters, setFilters] = useState<Record<string, any>>({
        status: ['대기', '반려', '추가견적'],
        create: 'ALL',
        update: 'ALL',
        confirm: 'ALL'
    });

    const resetAllFilters = () => {
        setIsResetting(true);

        // 필터 초기화
        setFilters({
            status: ['대기', '반려', '추가견적'],
            create: 'ALL',
            start: 'ALL',
            confirm: 'ALL',
            min: '',
            max: ''
        });

        // 날짜 범위 초기화
        setDateRanges({
            create: { start: null, end: null },
            update: { start: null, end: null },
            confirm: { start: null, end: null }
        });

        // 페이지 초기화
        setPage(1);

        // 정렬 초기화
        setSort({
            field: 'projectName',
            direction: 'asc'
        });

        // 다음 렌더링 사이클에서 리셋 상태 해제
        setTimeout(() => setIsResetting(false), 0);
    };

    //테이블 로우 선택 시
    const handleRowClick = (row: ProjectRequestTableResponseData) => {
        navigate(`/client/projects/project-request/${row.projectRequestId}`);
    };

    //프로젝트 요청 생성 페이지
    const handleAdd = () => {
        navigate("/client/projects/project-request");
    }

    //날짜 필터 값
    const [dateRanges, setDateRanges] = useState<DateRanges>({
        create: {start: null, end: null},
        update: {start: null, end: null},
        confirm: {start: null, end: null}
    });

    //정렬
    const [sort, setSort] = useState<{ field: string | null, direction: 'asc' | 'desc' | null }>({
        field: 'projectName',
        direction: 'asc'
    });

    //컬럼
    const columns: Column<ProjectRequestTableResponseData>[] = [
        {
            key: 'projectName',
            header: '프로젝트명',
            width: '15%',
            sortable: true
        },
        {
            key: 'requestStatus',
            header: '처리 상태',
            width: '10%'
        },
        {
            key: 'createdAt',
            header: '최초 요청일',
            width: '17%',
            sortable: true
        },
        {
            key: 'updatedAt',
            header: '최근 수정일',
            width: '17%',
            sortable: true
        },
        {
            key: 'confirmedAt',
            header: '처리 일시',
            width: '17%',
            sortable: true
        },
        {
            key: 'dataAmount',
            header: '데이터 수량',
            width: '10%',
            sortable: true
        },
        {
            key: 'projectType',
            header: '가공방식',
            width: '14%'
        }
    ];

    //프로젝트 추가 요청 상태 필터
    const statusFilterOptions = [
        {label: '대기', value: '대기'},
        {label: '승인', value: '승인'},
        {label: '반려', value: '반려'},
        {label: '추가견적', value: '추가견적'}
    ];

    //API 사용 위한 날짜 데이터 형식 변환
    const formatDateForAPI = (date: Date | string | null): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    //API 사용 위한 날짜 범위 데이터 형식 변환
    const formatDateRangeForAPI = (start: Date | null, end: Date | null): string | null => {
        //선택 안한 경우 'ALL'로 표시
        if (!start && !end) return 'ALL';
        if (!end) return `${formatDateForAPI(start)}~`;
        //시작날짜~종료날짜 형식으로 변환
        return `${formatDateForAPI(start)}~${formatDateForAPI(end)}`;
    };

    //날짜 형식 변환
    const handleDateRangeChange = (key: string, start: Date | null, end: Date | null) => {
        if (isResetting) return;
        setDateRanges(prev => ({
            ...prev,
            [key]: {start, end}
        }));

        const dateRangeString = formatDateRangeForAPI(start, end);
        setFilters(prev => ({
            ...prev,
            [key]: dateRangeString
        }));
        setPage(1);
    };

    //테이블 필터 설정
    const tableFilters = [
        {
            key: 'status',
            label: '처리 여부',
            options: statusFilterOptions,
            multiple: true,
            value: filters.status,
            component: undefined
        },
        {
            key: 'create',
            label: '최초 요청일',
            component: (
                <DateRangeFilter
                    label="최초 요청일"
                    startDate={dateRanges.create.start}
                    endDate={dateRanges.create.end}
                    onDateChange={(start, end) => handleDateRangeChange('create', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'update',
            label: '최근 수정일',
            component: (
                <DateRangeFilter
                    label="최근 수정일"
                    startDate={dateRanges.update.start}
                    endDate={dateRanges.update.end}
                    onDateChange={(start, end) => handleDateRangeChange('update', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'confirm',
            label: '처리일',
            component: (
                <DateRangeFilter
                    label="처리일"
                    startDate={dateRanges.confirm.start}
                    endDate={dateRanges.confirm.end}
                    onDateChange={(start, end) => handleDateRangeChange('confirm', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        }
    ];

    //정렬 기준 설정
    const getSortParam = (field: string | null, direction: 'asc' | 'desc' | null): ProjectRequestSort => {
        if (!field || !direction) return ProjectRequestSort.NAME_ASC;
        const sortMap: Record<string, Record<string, ProjectRequestSort>> = {
            projectName: {
                asc: ProjectRequestSort.NAME_ASC,
                desc: ProjectRequestSort.NAME_DESC
            },
            createdAt: {
                asc: ProjectRequestSort.CREATE_ASC,
                desc: ProjectRequestSort.CREATE_DESC
            },
            updatedAt: {
                asc: ProjectRequestSort.UPDATE_ASC,
                desc: ProjectRequestSort.UPDATE_DESC
            },
            confirmedAt: {
                asc: ProjectRequestSort.CONFIRM_ASC,
                desc: ProjectRequestSort.CONFIRM_DESC
            },
            dataAmount: {
                asc: ProjectRequestSort.AMOUNT_ASC,
                desc: ProjectRequestSort.AMOUNT_DESC
            }
        };
        return sortMap[field]?.[direction] || ProjectRequestSort.NAME_ASC;
    };

    //데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //클라이언트 프로젝트 추가 요청 테이블 조회 API
            const response = await projectRequestTableAPI({
                page: page - 1,
                sort: getSortParam(sort.field, sort.direction),
                status: filters.status as RequestStatus[],
                create: filters.create,
                update: filters.update,
                confirm: filters.confirm
            });
            //데이터 설정
            setData(response.content);
            //페이지 설정
            setTotalPages(Math.ceil(response.total / 7));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, sort, filters]);

    //필터값 변경
    const handleFilterChange = (key: string, value: any) => {
        //프로젝트 추가 요청 처리 상태 필터 변경
        setFilters(prev => {
            if (key === 'status') {
                const newStatuses = value;
                if (newStatuses.length === 0) {
                    return prev;
                }
                return {...prev, status: newStatuses};
            }
            return {...prev, [key]: value};
        });
        setPage(1);
    };

    //정렬 변경
    const handleSortChange = (field: string | null, direction: 'asc' | 'desc' | null) => {
        setSort({field, direction});
    };

    return (
        <div className={styles.container}>
            <div className={styles.flexContainer}>
                <h3>프로젝트 추가 요청</h3>
                <div>
                        <Button
                        variant="primary"
                        onClick={handleAdd}
                        size={'medium'}
                    >
                        추가
                    </Button>
                </div>
            </div>
            {/*테이블*/}
                <Table
                    columns={columns}
                    data={data}
                    loading={loading}
                    size="medium"
                    filters={tableFilters}
                    showPagination={true}
                    page={page}
                    pageSize={7}
                    totalItems={totalPages * 7}
                    onPageChange={setPage}
                    onFilterChange={handleFilterChange}
                    onSortChange={handleSortChange}
                    onRowClick={handleRowClick}
                    onReset={resetAllFilters}
                />
            </div>
            );
            };

            export default ClientProjectRequestTable;